import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Bath, PlusCircle, Trash2, Edit } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { filterBanos, createBano, updateBano, deleteBano } from '@/services/banos.service';
import { getGarmentTypes } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';
import { getConsumables } from '@/pages/parametrizacion/consumables/Services/consumables.services';
import BanoFormModal from './components/BanoFormModal';

const BanosPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [prendasParametrizadas, setPrendasParametrizadas] = useState([]);
  const [loadingPrendas, setLoadingPrendas] = useState(false);
  const [insumosParametrizados, setInsumosParametrizados] = useState([]);
  const [loadingInsumos, setLoadingInsumos] = useState(false);
  const { toast } = useToast();

  const columns = [
    {
      id: 'nombre',
      label: 'Nombre',
      sortable: true,
      render: (row) => (
        <div className="font-medium">{row.nombre}</div>
      )
    },
    {
      id: 'descripcion',
      label: 'Descripción',
      sortable: false,
      render: (row) => (
        <div className="text-sm text-gray-600">{row.descripcion || '-'}</div>
      )
    },
    {
      id: 'prendas',
      label: 'Prendas',
      sortable: false,
      render: (row) => (
        <div className="text-sm">
          {row.prendas && row.prendas.length > 0 ? (
            <span className="text-gray-700">{row.prendas.length} prenda(s)</span>
          ) : (
            <span className="text-gray-400">Sin prendas</span>
          )}
        </div>
      )
    },
    {
      id: 'insumos',
      label: 'Insumos',
      sortable: false,
      render: (row) => (
        <div className="text-sm">
          {row.insumos && row.insumos.length > 0 ? (
            <span className="text-amber-700 font-medium">{row.insumos.length} insumo(s)</span>
          ) : (
            <span className="text-gray-400">Sin insumos</span>
          )}
        </div>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.estado === 'Activo' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {row.estado}
        </span>
      )
    },
    {
      id: 'actions',
      key: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'edit',
          icon: 'Edit',
          tooltip: 'Editar cama',
          className: 'hover:bg-blue-100 text-blue-600'
        },
        {
          key: 'delete',
          icon: 'Trash',
          tooltip: 'Eliminar cama',
          className: 'hover:bg-red-100 text-red-600'
        }
      ]
    }
  ];

  const fetchPrendasParametrizadas = useCallback(async () => {
    setLoadingPrendas(true);
    try {
      const response = await getGarmentTypes({
        page: 1,
        limit: 1000, // Cargar todas las prendas activas
        query: { estado: 'Activo' }
      });
      
      const prendasFormateadas = (response.data || []).map(prenda => ({
        id: prenda._id,
        nombre: prenda.nombre,
        peso: prenda.peso / 1000, // Convertir gramos a kilogramos
        descripcion: prenda.descripcion || ''
      }));
      
      setPrendasParametrizadas(prendasFormateadas);
    } catch (error) {
      console.error('Error al cargar prendas parametrizadas:', error);
      toast({
        title: 'Advertencia',
        description: 'No se pudieron cargar las prendas parametrizadas.',
        variant: 'destructive',
      });
    } finally {
      setLoadingPrendas(false);
    }
  }, [toast]);

  const fetchInsumosParametrizados = useCallback(async () => {
    setLoadingInsumos(true);
    try {
      const response = await getConsumables({
        page: 1,
        limit: 1000,
        query: { estado: 'Activo' }
      });

      const insumosFormateados = (response.data || []).map(insumo => ({
        id: insumo._id,
        nombre: insumo.nombre,
        descripcion: insumo.descripcion || '',
        tipo: insumo.tipo || ''
      }));

      setInsumosParametrizados(insumosFormateados);
    } catch (error) {
      console.error('Error al cargar insumos parametrizados:', error);
      toast({
        title: 'Advertencia',
        description: 'No se pudieron cargar los insumos parametrizados.',
        variant: 'destructive',
      });
    } finally {
      setLoadingInsumos(false);
    }
  }, [toast]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await filterBanos({ limit: itemsPerPage, offset: currentPage, query: {} });
      setData(Array.isArray(response.data) ? response.data : []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar baños:', error);
      setData([]);
      setTotalRecords(0);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los baños.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, toast]);

  // Cargar prendas e insumos parametrizados al montar el componente
  useEffect(() => {
    fetchPrendasParametrizadas();
    fetchInsumosParametrizados();
  }, [fetchPrendasParametrizadas, fetchInsumosParametrizados]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await updateBano(editingItem._id, formData);
        toast({ title: 'Éxito', description: 'Baño actualizado correctamente.' });
      } else {
        await createBano(formData);
        toast({ title: 'Éxito', description: 'Baño creado correctamente.' });
      }
      fetchData();
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: `No se pudo guardar el baño. ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await deleteBano(itemToDelete._id);
      toast({ title: 'Éxito', description: 'Baño eliminado correctamente.' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: `No se pudo eliminar el baño. ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAction = (actionKey, row) => {
    if (actionKey === 'edit') {
      handleEdit(row);
    } else if (actionKey === 'delete') {
      handleDeleteClick(row);
    }
  };

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto p-6 space-y-6"
    >
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-teal-100 rounded-full">
                <Bath className="h-8 w-8 text-teal-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Gestión de Baños
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Administra los tipos de baños y sus prendas asociadas
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleCreate}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Nuevo Baño
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={data}
            isLoading={loading}
            onAction={handleAction}
            emptyMessage="No hay baños registrados"
          />
          
          {totalRecords > 0 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalRecords={totalRecords}
                recordsPerPage={itemsPerPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <BanoFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        editingItem={editingItem}
        prendasParametrizadas={prendasParametrizadas}
        loadingPrendas={loadingPrendas}
        insumosParametrizados={insumosParametrizados}
        loadingInsumos={loadingInsumos}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente el baño "{itemToDelete?.nombre}".
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default BanosPage;
